<template>
  <DetailSplitView :ready="listingReady" :show-panel="Boolean(panel)">
    <template #top>
      <slot name="navigation" />

      <template v-if="listingReady">
        <template v-if="listing.isInactive || listing.hasDeferral">
          <StatusBadge v-if="listing.isInactive" variant="danger">Inactive</StatusBadge>
          <StatusBadge v-if="listing.isWhitelisted" variant="warning">
            Deferred{{ listing.deferralEndDate ? ` until ${listing.deferralEndDate.format('MMM D, YYYY')}` : '' }}
          </StatusBadge>
          <StatusBadge v-else-if="listing.hasEndedDeferral" variant="secondary">
            Deferral ended{{ listing.deferralEndDate ? ` ${listing.deferralEndDate.format('MMM D, YYYY')}` : '' }}
          </StatusBadge>
        </template>

        <div class="ms-auto d-flex flex-row align-items-center">

          <IconButton
            :icon="listing.isPropertyVerified ? 'house-fill' : 'house'"
            :title="listing.isPropertyVerified ? 'Address verified' : 'Verify address'"
            :class="['flex-shrink-0 panel-tab', { 'panel-tab--active': panel === 'verification' }]"
            @click="togglePanel('verification')"
          />
          <IconButton
            icon="chat"
            :title="(listing.commentCount || 'No') + ' Comment' + (listing.commentCount === 1 ? '' : 's')"
            :class="['flex-shrink-0 panel-tab', { 'panel-tab--active': panel === 'comments' }]"
            @click="togglePanel('comments')"
          />
          <IconButton
            :icon="listing.isWhitelisted ? 'clock-fill' : listing.hasEndedDeferral ? 'clock-history' : 'clock'"
            :title="listing.isWhitelisted ? 'Deferred / Whitelisted' : listing.hasEndedDeferral ? 'Previously deferred' : 'Defer / Whitelist'"
            :class="['flex-shrink-0 panel-tab', { 'panel-tab--active': panel === 'whitelist' }]"
            @click="togglePanel('whitelist')"
          />
          <IconButton
            icon="info-circle"
            title="Listing source & status"
            :class="['flex-shrink-0 panel-tab', { 'panel-tab--active': panel === 'status' }]"
            @click="togglePanel('status')"
          />

          <div class="dropdown">
            <IconButton
              button
              icon="three-dots"
              class="flex-shrink-0 dropdown-toggle no-caret"
              data-bs-toggle="dropdown"
            />
            <ul class="dropdown-menu">
              <li>
                <IconButton
                  icon="geo-alt"
                  text="Re-center map"
                  @click="showLocation"
                  small align="start" class="dropdown-item"
                />
              </li>
              <li>
                <IconButton
                  v-if="listing.isSeen"
                  icon="dot"
                  text="Mark as new"
                  @click="toggleSeen"
                  small align="start" class="dropdown-item"
                />
              </li>
              <li>
                <IconButton
                  v-if="showDeleteButton"
                  icon="trash"
                  text="Delete this listing"
                  @click="deleteListing"
                  variant="danger"
                  small align="start" class="dropdown-item"
                />
              </li>
            </ul>
          </div>
        </div>
      </template>
    </template>

    <template #after-top>
      <BCollapse :show="scrolled && !!listing.listingTitle" class="border-bottom">
        <a :href="safeHref(listing.url)" target="_blank" class="btn btn-flat d-flex flex-row align-items-center" style="text-overflow: ellipsis" rel="noopener noreferrer">
          <ListingSourceIcon :listing="listing" class="me-1"/>
          <div class="small fw-semibold text-nowrap ellipsis overflow-hidden me-1">
            {{ listing.listingTitle }}
          </div>
          <div v-if="listing.listingHost" class="small text-nowrap ellipsis">
            by {{ listing.listingHost }}
          </div>
        </a>
      </BCollapse>
    </template>

    <template #left>
      <div class="flex-grow-1 overflow-auto" @scroll="updateScroll" ref="scroller">
        <ListingView :listing="listing" />
      </div>
    </template>

    <template #right>
      <ListingAddressEditor
        v-if="panel === 'verification'"
        :listing="listing"
        :model="model"
        :selected-license="selectedLicense"
        @show-location="args => $emit('showLocation', args)"
        @open-street-view="args => $emit('openStreetView', args)"
      />
      <ActivityPanel
        v-else-if="panel === 'comments'"
        :model="listing"
        class="h-100"
      />
      <WhitelistEditor
        v-else-if="panel === 'whitelist'"
        :model="listing"
        noun="listing"
        class="h-100"
      />
      <ListingStatusView
        v-else-if="panel === 'status'"
        :listing="listing"
        class="h-100"
      />
    </template>

    <template #fallback>
      <div class="d-flex flex-column align-items-center justify-content-center flex-grow-1 p-4">
        <LoadingMessage v-if="loadingListing" message="Loading listing" />
        <div v-else-if="listingLoadError" class="text-danger text-center small">
          Could not load listing details.
        </div>
        <div v-else class="text-muted text-center small">
          Listing not found.
        </div>
      </div>
    </template>
  </DetailSplitView>
</template>

<script>
import { safeHref } from '@/safeRendering';
import {Listing} from "@/models/Listing";
import ListingSourceIcon from "@/components/common/ListingSourceIcon.vue";
import BCollapse from "@/components/common/BCollapse.vue";
import IconButton from "@/components/common/IconButton.vue";
import WhitelistEditor from "@/components/common/WhitelistEditor.vue";
import ListingView from "@/components/listing/ListingView.vue";
import ListingAddressEditor from "@/components/listing/ListingAddressEditor.vue";
import DetailSplitView from "@/components/common/DetailSplitView.vue";
import {License} from "@/models/License";
import ActivityPanel from "@/components/common/ActivityPanel.vue";
import {LocalStorage} from "@/models/LocalStorage";
import StatusBadge from "@/components/common/StatusBadge.vue";
import ListingStatusView from "@/components/listing/ListingStatusView.vue";
import LoadingMessage from "@/components/common/LoadingMessage.vue";

const NOTES_TAB_KEY = 'listingNotesEnabled';

export default {
  components: {
    DetailSplitView,
    LoadingMessage,
    ListingStatusView,
    StatusBadge,
    ActivityPanel,
    ListingAddressEditor,
    ListingView,
    WhitelistEditor,
    IconButton,
    BCollapse,
    ListingSourceIcon,
  },
  emits: ['close','showLocation','openStreetView','update:panel', 'loaded'],
  props: {
    city: Object,
    listingId: String,
    selectedLicense: License,
    panel: String
  },
  data() {
    return {
      listing: null,
      loadingListing: false,
      listingLoadError: null,
      loadToken: 0,
      scrolled: false
    };
  },
  watch: {
    loadKey: {
      immediate: true,
      handler() {
        this.loadListing();
      }
    }
  },
  methods: {
    safeHref,

    async loadListing() {
      const token = ++this.loadToken;
      this.loadingListing = true;
      this.listingLoadError = null;

      if (!this.city || !this.listingId) {
        this.listing = null;
        this.loadingListing = false;
        return;
      }
      this.listing = null;

      try {
        const listing = await Listing.one(this.city, this.listingId);
        if (token !== this.loadToken) {
          return;
        }
        const isDifferentListing = listing?.id !== this.listing?.id;
        this.listing = listing;
        this.$emit('loaded', listing);
        await this.reload(isDifferentListing);
      } catch (error) {
        if (token !== this.loadToken) {
          return;
        }
        console.error(`Could not load listing ${this.listingId}`, error);
        this.listing = null;
        this.listingLoadError = error;
      } finally {
        if (token === this.loadToken) {
          this.loadingListing = false;
        }
      }
    },
    async reload(isDifferentListing) {
      if (!this.listing) {
        return;
      }
      if (isDifferentListing) {
        this.showLocation();

        // Auto-open default panel for new listings (only if no panel specified)
        if (!this.panel && this.listing) {
          this.$emit('update:panel', 'verification');
        }
      }
      if (this.$refs.scroller) {
        this.$refs.scroller.scrollTo(0, 0);
      }
      if (isDifferentListing) {
        await this.listing.updateSeen(true);
      }
    },
    showLocation() {
      if (this.listing?.latlng) {
        this.$emit('showLocation', this.listing.latlng);
      }
    },
    close(message) {
      this.$emit('close', message || false);
    },
    updateScroll(e) {
      this.scrolled = e.target.scrollTop >= 50;
    },
    deleteListing() {
      if (this.listing) {
        this.listing.delete()
        .then(() => this.$emit('close'));
      }
    },
    toggleSeen() {
      if (this.listing) {
        this.listing.updateSeen(!this.listing.isSeen);
      }
    },
    togglePanel(panelName) {
      this.$emit('update:panel', this.panel === panelName ? null : panelName);
    },
    closePanel() {
      this.$emit('update:panel', null);
    },
    updateActiveStatus(active) {
      // TODO add confirmation dialog here
      this.listing.updateActiveStatus(active);
    }
  },
  computed: {
    loadKey() {
      return `${this.city?.id || ''}/${this.listingId || ''}`;
    },
    listingReady() {
      return Boolean(this.listing?.hasData);
    },
    model() {
      return this.listing;
    },
    showVerificationButton() {
      return true;
      // return !this.listing.isPropertyVerified;
    },
    showDeleteButton() {
      return false;
      // const user = Session.currentUser.value;
      // return user && user.role === 'admin';
    },
    isNotesTabEnabled() {
      return LocalStorage.get(NOTES_TAB_KEY) || false;
    }
  }
}
</script>

<style scoped>
</style>
